// StudioCreateMenu — the Studio's Create menu with its next steps (pen
// "Studio · Create menu", spec §5; #317; the steps of Create from a source
// pen Q73), inside a popover its host opens: from "+ Create" at the
// outline's foot, the first line of an empty page or the + in a line's
// margin, or at the caret once "/" picked Create from a source…. It starts
// on the one Create menu (CreateMenu.tsx: a filter field, Ask here, Create
// from a source…, the kinds; never Course, never Question, which needs a
// caret) and walks, every step inside the same menu:
//  - Ask here: no step here; the menu closes and the request is written in
//    a line where the part would land (pen Q66; AskLine.tsx), starting
//    with the words typed in the filter;
//  - Create from a source…: step 1, the project's sources with a filter
//    ("Find a source") and Add a source… at the foot; step 2, what to make
//    from the picked one (Page, Quiz) or Ask here for that source only;
//  - Change (in the foot): where it lands, from every course and module.
// A step's head goes back one step (as Escape does, in the host's
// popover). The foot, "Lands after <part> · Change", shows only when
// opened from a button: at the caret there is no foot (spec §5).
//
// Pure: the place, the sources and the step are projected in; every pick
// goes out as an intent. Local state is view-ephemeral only, and goes when
// the popover closes: the filters, the place picked with Change, and the
// keyboard cursor of each step.

export type { CreateOpening };
export { StudioCreateMenu };

import { exhaustSwitchCase } from "@bilda/utils";
import { useLingui } from "@lingui/react/macro";
import { type ReactNode, useId, useState } from "react";
import { CreateMenu, type CreateMenuRow } from "../CreateMenu/CreateMenu.js";
import {
    menuRowOf,
    slashRowsFor,
    slashStartAt,
} from "../Editor/SlashMenu/slashItems.js";
import { Icon, type IconName } from "../Icon/Icon.js";
import {
    type CreateKind,
    type CreateStep,
    type FromSourceKind,
    fromSourceKinds,
    type Landing,
    type LandingChoice,
    type SourceKind,
    type SourceRow,
    sourceRowsMatching,
} from "./studioCreate.js";
import { studioCreateItems, studioSlashKeyAt } from "./studioSlash.js";

// Where it was opened: a button (with the foot) or the caret (without).
type CreateOpening = "button" | "caret";

type CreateMenuIntents = {
    readonly onStep: (step: CreateStep) => void;
    // The head's back chevron (the host knows where back goes).
    readonly onBack: () => void;
    // Ask here: a line where the part would land, starting with `words`;
    // for `source` only, when one was picked (absent: the project's).
    readonly onAskHere: (
        words: string,
        container: string | undefined,
        source: string | undefined,
    ) => void;
    readonly onInsert: (
        kind: CreateKind,
        container: string | undefined,
    ) => void;
    readonly onFromSource: (
        source: string,
        kind: FromSourceKind,
        container: string | undefined,
    ) => void;
    // Add a source…: the ordinary add flow (from the computer).
    readonly onAddSource: () => void;
};

const sourceIconOf = (kind: SourceKind): IconName => {
    switch (kind.type) {
        case "slides":
            return "presentation";
        case "image":
            return "image";
        case "pdf":
        case "document":
        case "other":
        case "none":
            return "file-text";
        default:
            return exhaustSwitchCase(kind);
    }
};

const makeIconOf = (kind: FromSourceKind): IconName => {
    switch (kind) {
        case "page":
            return "book-open";
        case "quiz":
            return "mcq";
        default:
            return exhaustSwitchCase(kind);
    }
};

// ↑/↓ over `count` rows, wrapping.
const moved = (i: number, direction: "up" | "down", count: number): number =>
    count === 0 ? 0 : (i + (direction === "down" ? 1 : -1) + count) % count;

const StudioCreateMenu = ({
    step,
    opening,
    landing,
    choices,
    sources,
    online,
    intents,
}: {
    // The step shown (`shownStep`).
    readonly step: CreateStep;
    readonly opening: CreateOpening;
    // Where a part lands by default (absent: nowhere yet).
    readonly landing: Landing | undefined;
    // Every container one can pick with Change.
    readonly choices: readonly LandingChoice[];
    // The project's sources, in the order step 1 lists them.
    readonly sources: readonly SourceRow[];
    readonly online: boolean;
    readonly intents: CreateMenuIntents;
}): ReactNode => {
    const { t, i18n } = useLingui();
    const listboxId = useId();
    const [chosen, setChosen] = useState<LandingChoice | undefined>(undefined);
    const [query, setQuery] = useState("");
    const [active, setActive] = useState(0);
    const [sourceQuery, setSourceQuery] = useState("");
    const [sourceActive, setSourceActive] = useState(0);
    const [makeActive, setMakeActive] = useState(0);
    const container = chosen?.id ?? landing?.id;
    // The rows the filter field keeps (Ask here shows the words typed).
    const items = slashRowsFor(studioCreateItems(i18n), query);

    const pick = (index: number) => {
        const intent = items[index]?.intent;
        const key =
            intent?.type === "surface"
                ? studioSlashKeyAt(intent.at)
                : undefined;
        if (key === undefined) return;
        switch (key.type) {
            case "ask_here":
                return intents.onAskHere(query.trim(), container, undefined);
            case "from_source":
                return intents.onStep({ type: "sources" });
            case "insert":
                return intents.onInsert(key.kind, container);
            default:
                return exhaustSwitchCase(key);
        }
    };

    const foot =
        opening === "caret" ? undefined : (
            <>
                <span className="bilda-create__lands">
                    {chosen !== undefined
                        ? t`Lands in ${chosen.title}`
                        : landing === undefined
                          ? t`Lands in a new module`
                          : landing.after === undefined
                            ? t`Lands in ${landing.title}`
                            : t`Lands after ${landing.after}`}
                </span>
                {choices.length > 1 ? (
                    <button
                        type="button"
                        className="bilda-create__change"
                        onClick={() => intents.onStep({ type: "place" })}
                    >
                        {t`Change`}
                    </button>
                ) : null}
            </>
        );

    const kindWord = (kind: SourceKind): string | undefined => {
        switch (kind.type) {
            case "pdf":
                return t`PDF`;
            case "slides":
                return t`Slides`;
            case "document":
                return t({ context: "source kind", message: "Document" });
            case "image":
                return t`Image`;
            case "other":
                return kind.tag;
            case "none":
                return undefined;
            default:
                return exhaustSwitchCase(kind);
        }
    };

    const makeRow = (kind: FromSourceKind): CreateMenuRow => {
        switch (kind) {
            case "page":
                return {
                    key: kind,
                    group: "make",
                    label: t`Page`,
                    name: t`Page`,
                    hint: t`A page to read, from this source`,
                    icon: makeIconOf(kind),
                    more: false,
                };
            case "quiz":
                return {
                    key: kind,
                    group: "make",
                    label: t`Quiz`,
                    name: t`Quiz`,
                    hint: t`Questions on this source`,
                    icon: makeIconOf(kind),
                    more: false,
                };
            default:
                return exhaustSwitchCase(kind);
        }
    };

    // Step 1: the sources the filter keeps, then Add a source….
    const sourcesStep = () => {
        const kept = sourceRowsMatching(sources, sourceQuery);
        const rows: readonly CreateMenuRow[] = [
            ...kept.map((s): CreateMenuRow => {
                const word = kindWord(s.kind);
                return {
                    key: `source:${s.id}`,
                    group: "sources",
                    label: s.title,
                    name: s.title,
                    ...(word === undefined ? {} : { hint: word }),
                    icon: sourceIconOf(s.kind),
                    more: false,
                };
            }),
            {
                key: "add-source",
                group: "after",
                label: t`Add a source…`,
                name: t`Add a source…`,
                hint:
                    sources.length === 0
                        ? t`No sources yet`
                        : t`From your computer`,
                icon: "plus",
                more: false,
            },
        ];
        const pickSource = (index: number) => {
            const picked = kept[index];
            if (picked !== undefined)
                return intents.onStep({ type: "make", source: picked.id });
            if (index === kept.length) intents.onAddSource();
        };
        return (
            <CreateMenu
                label={t`Create from a source`}
                head={{
                    title: t`Create from a source`,
                    onBack: intents.onBack,
                }}
                rows={rows}
                activeIndex={sourceActive}
                listboxId={listboxId}
                onPick={pickSource}
                onHover={setSourceActive}
                keys={{
                    type: "filter",
                    value: sourceQuery,
                    placeholder: t`Find a source`,
                    onChange: (next) => {
                        setSourceQuery(next);
                        setSourceActive(0);
                    },
                    onMove: (direction) =>
                        setSourceActive((i) =>
                            moved(i, direction, rows.length),
                        ),
                    onPickActive: () => pickSource(sourceActive),
                }}
                disabled={!online}
            />
        );
    };

    // Step 2: what to make from the picked source, or Ask here for it.
    const makeStep = (source: SourceRow) => {
        const rows: readonly CreateMenuRow[] = [
            ...fromSourceKinds.map(makeRow),
            {
                key: "ask-source",
                group: "after",
                label: t`Ask here`,
                name: t`Ask here`,
                hint: t`Say what to make from it`,
                icon: "ask",
                more: false,
            },
        ];
        const pickMake = (index: number) => {
            const kind = fromSourceKinds[index];
            if (kind !== undefined)
                return intents.onFromSource(source.id, kind, container);
            if (index === fromSourceKinds.length)
                intents.onAskHere("", container, source.id);
        };
        return (
            <CreateMenu
                label={source.title}
                head={{ title: source.title, onBack: intents.onBack }}
                rows={rows}
                activeIndex={makeActive}
                listboxId={listboxId}
                onPick={pickMake}
                onHover={setMakeActive}
                keys={{
                    type: "list",
                    onMove: (direction) =>
                        setMakeActive((i) => moved(i, direction, rows.length)),
                    onPickActive: () => pickMake(makeActive),
                }}
                disabled={!online}
            />
        );
    };

    switch (step.type) {
        case "start":
            return (
                <CreateMenu
                    label={t`Create`}
                    rows={items.map(menuRowOf)}
                    activeIndex={active}
                    listboxId={listboxId}
                    onPick={pick}
                    onHover={setActive}
                    keys={{
                        type: "filter",
                        value: query,
                        placeholder: t`Type to filter`,
                        onChange: (next) => {
                            setQuery(next);
                            setActive(
                                slashStartAt(
                                    slashRowsFor(studioCreateItems(i18n), next),
                                    next,
                                ),
                            );
                        },
                        onMove: (direction) =>
                            setActive((i) => moved(i, direction, items.length)),
                        onPickActive: () => pick(active),
                    }}
                    disabled={!online}
                    foot={foot}
                />
            );
        case "sources":
        case "adding":
            return sourcesStep();
        case "make": {
            // A source gone from the project meanwhile: back to the list.
            const source = sources.find((s) => s.id === step.source);
            return source === undefined ? sourcesStep() : makeStep(source);
        }
        case "place":
            return (
                <div className="bilda-create-menu bilda-create">
                    <button
                        type="button"
                        className="bilda-create-menu__head"
                        aria-label={`${t`Back`}: ${t`Where it lands`}`}
                        onClick={intents.onBack}
                    >
                        <Icon name="chevron-left" size="xs" />
                        <span className="bilda-create-menu__title">
                            {t`Where it lands`}
                        </span>
                    </button>
                    <ul className="bilda-create__rows bilda-create__rows--scroll">
                        {choices.map((c) => (
                            <li
                                key={c.id}
                                style={{
                                    paddingInlineStart: `calc(var(--space-3) * ${c.depth})`,
                                }}
                            >
                                <button
                                    type="button"
                                    className="bilda-create__row"
                                    aria-current={
                                        c.id === container ? "true" : undefined
                                    }
                                    onClick={() => {
                                        setChosen(c);
                                        intents.onStep({ type: "start" });
                                    }}
                                >
                                    <Icon name="folder" size="sm" />
                                    <span className="bilda-create__label">
                                        {c.title}
                                    </span>
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>
            );
        default:
            return exhaustSwitchCase(step);
    }
};
